import { Selector } from '@ngxs/store';
import {
	MatterStatementOfAccount,
	StatementOfAccountDefaultTypes,
	StatementOfAccountType,
} from '@models/statementOfAccount';
import { CosmolexAccountingItem } from '../../../../private/sale/accounting/tables/cosmolex-accounting-items-table/cosmolex-accounting-items-table.component';
import { TagsSelector } from '@state/global/tags/tags.selector';
import { MatterJurisdictionSelectors } from '../jurisdictions/jurisdictions.selectors';
import { ComplianceRequest, Jurisdiction } from '@models/jurisdictions';
import { getJurisdictionAndDepartmentByComplianceRequest } from '@core/util/jurisdictions';
import { replaceByCollectionContext, replaceGroupId } from '@core/util/string';
import { MatterStatementofAccountSelectors } from '../statementOfAccounts/statementOfAccounts.selectors';
import { LeapCostRecoveryItem, LeapFeeTaskCodeItem } from './accounting.models';
import {
	GhostPracticeItem,
	SolunoItem,
} from 'src/app/private/sale/accounting/accounting.models';

export class MatterAccountingSelectors {
	@Selector([MatterAccountingSelectors.getAllWithParsedDescriptions])
	static getNonZeroNonHstItems(
		items: MatterStatementOfAccount[],
	): MatterStatementOfAccount[] {
		return items
			?.filter((x) => x.price > 0 && !x.deleted)
			?.filter((x) => x.type === StatementOfAccountType.NON_HST);
	}

	@Selector([MatterAccountingSelectors.getAllWithParsedDescriptions])
	static getNonZeroHstItems(
		items: MatterStatementOfAccount[],
	): MatterStatementOfAccount[] {
		return items
			?.filter((x) => x.price > 0 && !x.deleted)
			?.filter((x) => x.type === StatementOfAccountType.HST);
	}

	@Selector([MatterAccountingSelectors.getAllWithParsedDescriptions])
	static getNonZeroConvenyancingFeeItems(
		items: MatterStatementOfAccount[],
	): MatterStatementOfAccount[] {
		return items
			?.filter((x) => x.price > 0 && !x.deleted)
			?.filter((x) => x.type === StatementOfAccountType.FEE);
	}

	@Selector([MatterAccountingSelectors.getNonZeroNonHstItems])
	static getCosmolexNonZeroNonHstItems(
		items: MatterStatementOfAccount[],
	): CosmolexAccountingItem[] {
		return items?.map((x) => {
			return {
				title: x.description,
				amount: x.price,
				hst: false,
			};
		});
	}

	@Selector([MatterAccountingSelectors.getNonZeroHstItems])
	static getCosmolexNonZeroHstItems(
		items: MatterStatementOfAccount[],
	): CosmolexAccountingItem[] {
		return items?.map((x) => {
			return {
				title: x.description,
				amount: x.price,
				hst: true,
			};
		});
	}

	@Selector([MatterAccountingSelectors.getNonZeroConvenyancingFeeItems])
	static getCosmolexNonZeroConvenyancingFeeItems(
		items: MatterStatementOfAccount[],
	): CosmolexAccountingItem[] {
		return items?.map((x) => {
			return {
				title: x.description,
				amount: x.price,
				hst: false,
			};
		});
	}

	@Selector([MatterAccountingSelectors.getNonZeroNonHstItems])
	static getSolunoNonZeroNonHstItems(
		items: MatterStatementOfAccount[],
	): SolunoItem[] {
		return items?.map((x) => {
			return {
				accountingItem: {
					title: x.description,
					amount: x.price,
					activityCode: x.solunoActivityCode,
					statementId: x.id,
					hst: false,
				},
				statement: x,
			};
		});
	}

	@Selector([MatterAccountingSelectors.getNonZeroHstItems])
	static getSolunoNonZeroHstItems(
		items: MatterStatementOfAccount[],
	): SolunoItem[] {
		return items?.map((x) => {
			return {
				accountingItem: {
					title: x.description,
					amount: x.price,
					activityCode: x.solunoActivityCode,
					statementId: x.id,
					hst: true,
				},
				statement: x,
			};
		});
	}

	@Selector([MatterAccountingSelectors.getNonZeroConvenyancingFeeItems])
	static getSolunoNonZeroConvenyancingFeeItems(
		items: MatterStatementOfAccount[],
	): SolunoItem[] {
		return items?.map((x) => {
			return {
				accountingItem: {
					title: x.description,
					amount: x.price,
					activityCode: x.solunoActivityCode,
					statementId: x.id,
					hst: true,
				},
				statement: x,
			};
		});
	}

	@Selector([MatterAccountingSelectors.getNonZeroNonHstItems])
	static getGhostPracticeNonZeroNonHstItems(
		items: MatterStatementOfAccount[],
	): GhostPracticeItem[] {
		return items?.map((x) => {
			return {
				accountingItem: {
					title: x.description,
					amount: x.price,
					ghostPracticeCode: x.ghostPracticeCode,
					statementId: x.id,
					hst: false,
				},
				statement: x,
			};
		});
	}

	@Selector([MatterAccountingSelectors.getNonZeroHstItems])
	static getGhostPracticeNonZeroHstItems(
		items: MatterStatementOfAccount[],
	): GhostPracticeItem[] {
		return items?.map((x) => {
			return {
				accountingItem: {
					title: x.description,
					amount: x.price,
					ghostPracticeCode: x.ghostPracticeCode,
					statementId: x.id,
					hst: true,
				},
				statement: x,
			};
		});
	}

	@Selector([MatterAccountingSelectors.getNonZeroConvenyancingFeeItems])
	static getGhostPracticeNonZeroConvenyancingFeeItems(
		items: MatterStatementOfAccount[],
	): GhostPracticeItem[] {
		return items?.map((x) => {
			return {
				accountingItem: {
					title: x.description,
					amount: x.price,
					ghostPracticeCode: x.ghostPracticeCode,
					statementId: x.id,
					hst: true,
				},
				statement: x,
			};
		});
	}

	@Selector([MatterAccountingSelectors.getAllWithParsedDescriptions])
	static getHasInvalids(items: MatterStatementOfAccount[]): boolean {
		return items
			?.filter((x) => x.price > 0 && !x.deleted)
			?.some(
				(x) =>
					x.leap === null ||
					(x.leap.costRecoveryTaskCode === null && x.leap.feeTaskCode === null),
			);
	}

	@Selector([MatterAccountingSelectors.getNonZeroNonHstItems])
	static getLeapNonZeroNonHstItems(
		items: MatterStatementOfAccount[],
	): LeapCostRecoveryItem[] {
		return items?.map((x) => {
			return {
				accountingItem: {
					title: x.description,
					amount: x.price,
					activityCode: x.leap.costRecoveryTaskCode,
					statementId: x.id,
					hst: false,
				},
				statement: x,
			};
		});
	}

	@Selector([MatterAccountingSelectors.getNonZeroHstItems])
	static getLeapNonZeroHstItems(
		items: MatterStatementOfAccount[],
	): LeapCostRecoveryItem[] {
		return items?.map((x) => {
			return {
				accountingItem: {
					title: x.description,
					amount: x.price,
					activityCode: x.leap.costRecoveryTaskCode,
					statementId: x.id,
					hst: true,
				},
				statement: x,
			};
		});
	}

	@Selector([MatterAccountingSelectors.getNonZeroConvenyancingFeeItems])
	static getLeapNonZeroConvenyancingFeeItems(
		items: MatterStatementOfAccount[],
	): LeapFeeTaskCodeItem[] {
		return items?.map((x) => {
			return {
				accountingItem: {
					title: x.description,
					amount: x.price,
					activityCode: x.leap.feeTaskCode,
					statementId: x.id,
					hst: true,
				},
				statement: x,
			};
		});
	}

	@Selector([
		MatterStatementofAccountSelectors.getAll,
		TagsSelector.getTagInjectedDocument,
		MatterJurisdictionSelectors.getAll,
		MatterJurisdictionSelectors.getAllComplianceRequests,
	])
	public static getAllWithParsedDescriptions(
		items: MatterStatementOfAccount[],
		parsedDocumentFilterFn: (doc: string) => string,
		jurisdictions: Jurisdiction[],
		complianceRequests: ComplianceRequest[],
	): MatterStatementOfAccount[] {
		return items.map((item) => {
			let context = null;
			if (
				item.defaultType ===
					StatementOfAccountDefaultTypes.COMPLIANCE_REQUEST_FEE &&
				complianceRequests?.length > 0 &&
				jurisdictions?.length > 0
			) {
				const { jurisdiction, department } =
					getJurisdictionAndDepartmentByComplianceRequest(
						jurisdictions,
						complianceRequests.find((y) => y.id === item.refId),
					) ?? { department: null, jurisdiction: null };

				context = {
					complianceRequests: [{ referenceId: item?.refId }],
					jurisdictions: [{ referenceId: jurisdiction?.id }],
					departments: [{ referenceId: department?.id }],
				};
			}

			const parsedDescription = parsedDocumentFilterFn(
				context
					? replaceByCollectionContext(item.description, context)
					: (replaceGroupId(item.description, item.refId) as string),
			);

			return {
				...item,
				description: parsedDescription,
			};
		});
	}
}
